/* eslint-disable @typescript-eslint/naming-convention */
import { HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, skip, take } from 'rxjs/operators';
import { OrganisationInterface } from '../../../models/interface/organisation/organisation.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { UserInterface } from '../../../models/interface/user.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { GetAllUsersAdminPayload, UserActionTypes } from '../../../store/user/user.action';
import { OrganisationStore } from '../../../store/organisation/component-store/organisation.store';

@Component({
  selector: 'app-dialog-admin-organisation-create',
  templateUrl: './dialog-admin-organisation-create.component.html',
  styleUrls: ['./dialog-admin-organisation-create.component.scss'],
  providers: [OrganisationStore],
})
export class DialogAdminOrganisationCreateComponent implements OnInit, OnDestroy {
  public createOrganisationResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  // Form submission
  public groupFormType: UntypedFormGroup;
  public disabled = false;

  // Filter selection
  public filter = { userSelection: '' };

  public organisations: OrganisationInterface[] = [];
  public organisationsSubject = new BehaviorSubject<OrganisationInterface[]>([]);

  // Data from UserService
  public allECoachesAsAdmin$: Observable<UserInterface[]>;
  public allECoachesAsAdmin: UserInterface[] = [];

  private users$: Observable<UserInterface[]>;

  private profile$: Observable<ProfileInterface>;
  private profile: ProfileInterface = null;

  private allECoachesAsAdminSubject = new BehaviorSubject<UserInterface[]>([]);

  private createOrganisationResponse$: Observable<any>;

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private formBuilder: UntypedFormBuilder,
    private helperService: HelperService,
    private organisationStore: OrganisationStore,
    private store: Store<{
      myProfile: ProfileInterface;
      users: UserInterface[];
    }>,
    private dialogRef: MatDialogRef<DialogAdminOrganisationCreateComponent>,
  ) {
    this.allECoachesAsAdmin$ = this.allECoachesAsAdminSubject.asObservable();
    this.groupFormType = this.formBuilder.group({
      name: ['', [Validators.required, Validators.maxLength(255), Validators.minLength(1)]],
      owner_id: [-1],
    });
    this.groupFormType.controls['owner_id'].setValue(-1, { onlySelf: true });
    this.createOrganisationResponse$ = this.organisationStore.createOrganisationResponse$;
    this.users$ = store.select('users');
    this.profile$ = store.select('myProfile');
  }

  get f() {
    return this.groupFormType.controls;
  }

  public get helper() {
    return this.helperService;
  }

  ngOnInit(): void {
    this.profile$.pipe(
      filter((user) => user !== null),
    ).subscribe((profile) => {
      this.profile = profile;
    });
    const payload: GetAllUsersAdminPayload = {
      roles: 'ecoach',
      isVerified: 1,
    };
    this.store.dispatch({ type: UserActionTypes.getAllUsersAdminType, payload });
    this.subscriptions.push(
      this.users$.pipe(skip(1), take(1)).subscribe((result: UserInterface[]) => {
        this.allECoachesAsAdmin = result;
        this.allECoachesAsAdminSubject.next(this.allECoachesAsAdmin);
        if (this.allECoachesAsAdmin.length > 0) {
          if (this.profile) {
            const profileId = this.profile.id.toString();
            const selectedECoach =
              this.allECoachesAsAdmin.find((ecoach) => ecoach.id.toString() === profileId) ?? this.allECoachesAsAdmin[0];
            this.groupFormType.controls['owner_id'].setValue(selectedECoach.id, { onlySelf: true });
          } else {
            this.groupFormType.controls['owner_id'].setValue(this.allECoachesAsAdmin[0].id, { onlySelf: true });
          }
        }
      })
    );
  }

  isInvalidOwner(): boolean {
    return this.f.owner_id.invalid || this.f.owner_id.value < 0;
  }

  isGroupFormInvalid(): boolean {
    return this.isInvalidOwner() || this.f.name.invalid;
  }

  public createOrganisation(): void {
    if (this.isGroupFormInvalid() || this.createOrganisationResponse.value !== 'DEFAULT') {
      return;
    }
    this.createOrganisationResponse.next('LOADING');
    this.organisationStore.createOrganisationAdmin({
      name: this.f.name.value,
      ownerId: parseInt(this.f.owner_id.value.toString(), 10),
    });
    this.subscriptions.push(
      this.createOrganisationResponse$.pipe(skip(1), take(1)).subscribe((result) => {
        if (result instanceof HttpResponse) {
          this.createOrganisationResponse.next('SUCCESS');
          setTimeout(() => {
            this.createOrganisationResponse.next('DEFAULT');
            this.dialogRef.close();
          }, 2500);
        } else {
          this.createOrganisationResponse.next('FAILURE');
          setTimeout(() => {
            this.createOrganisationResponse.next('DEFAULT');
          }, 2500);
        }
      }),
    );
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
    this.allECoachesAsAdminSubject.next(this.allECoachesAsAdmin);
    this.organisationsSubject.next(this.organisations);
  }

  public onKeyFilter(): void {
    const userSelection = this.filter.userSelection.toLowerCase();
    const filterResults = this.allECoachesAsAdmin.filter(
      (user) =>
        user.attributes.name?.toLowerCase().includes(userSelection) ||
        user.attributes.email.toLowerCase().includes(userSelection),
    );
    const results =
      filterResults.length !== 0
        ? filterResults
        : [this.helper.findArrObjById(parseInt(this.f.owner_id.value.toString(), 10), this.allECoachesAsAdmin)];
    this.allECoachesAsAdminSubject.next(results);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
